import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { AcknowledgmentCampaign, PolicyAcknowledgment } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, policyApi } from "../../lib/api";
import { PolicyDemoBanner } from "../../components/PolicyMode";

const STATUS_TONE: Record<string, "neutral" | "success" | "warning" | "danger"> = {
  pending: "warning",
  acknowledged: "success",
  refused: "danger",
  reassigned: "neutral",
};

export function PolAcknowledgments() {
  const { activeOrg, user } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [refusalReason, setRefusalReason] = useState("");
  const [versionId, setVersionId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [deadline, setDeadline] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = useAsync(
    async () => {
      const [campaigns, mine, members, policies] = await Promise.all([
        policyApi.campaigns(orgId),
        policyApi.myAcknowledgments(orgId),
        api.members(orgId),
        policyApi.policies(orgId),
      ]);
      // A campaign may only target a published version, so the picker resolves
      // the published versions of every policy rather than asking for an id.
      const perPolicy = await Promise.all(
        policies.data.policies.map(async (policy) => {
          const versions = (await policyApi.versions(orgId, policy.id)).data.versions;
          return versions
            .filter((v) => v.status === "published")
            .map((v) => ({ id: v.id, label: `${policy.title} \u2014 v${v.versionNumber}` }));
        }),
      );
      return { campaigns, mine, members: members.members, publishedVersions: perPolicy.flat() };
    },
    [orgId],
  );

  const campaigns = state.data?.campaigns.data.campaigns ?? [];
  const mine = state.data?.mine.data.acknowledgments ?? [];
  const members = state.data?.members ?? [];
  const publishedVersions = state.data?.publishedVersions ?? [];
  const memberName = (userId: string) => {
    const match = members.find((m) => m.id === userId);
    return match ? match.displayName || match.email : userId;
  };

  const evidence = useAsync(
    async () => (selected ? policyApi.campaignEvidence(orgId, selected) : null),
    [orgId, selected],
  );

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Acknowledgments</h1>
          <p>Campaigns against published versions, recipient evidence, and your own outstanding items.</p>
        </div>
      </div>

      <PolicyDemoBanner show={state.data?.campaigns.demoMode ?? false} />
      {error && <ErrorState description={error} />}

      <Card>
        <CardHeader title="Launch a campaign" />
        <CardBody>
          <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap" }}>
            <Field label="Published version">
              {(id) => (
                <Select id={id} value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                  <option value="">Select a published version…</option>
                  {publishedVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Campaign name">
              {(id) => <TextInput id={id} value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />}
            </Field>
            <Field label="Recipient">
              {(id) => (
                <Select id={id} value={recipientId} onChange={(e) => setRecipientId(e.target.value)}>
                  <option value="">Select a teammate…</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.email}
                      {m.id === user?.id ? " (you)" : ""}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Deadline">
              {(id) => <TextInput id={id} type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />}
            </Field>
            <Button
              size="sm"
              disabled={busy || !versionId.trim() || !campaignName.trim() || !recipientId.trim() || !deadline}
              onClick={() =>
                run(async () => {
                  await policyApi.createCampaign(orgId, versionId.trim(), {
                    name: campaignName.trim(),
                    deadlineAt: new Date(`${deadline}T00:00:00.000Z`).toISOString(),
                    requireRefusalReason: true,
                    recipients: [{ userId: recipientId.trim() }],
                  });
                  setCampaignName("");
                  setRecipientId("");
                  state.reload();
                })
              }
            >
              Launch campaign
            </Button>
          </div>
        </CardBody>
      </Card>

      {state.loading && <LoadingState label="Loading acknowledgments…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}

      <Card>
        <CardHeader title="Campaigns" />
        <CardBody>
          {campaigns.length === 0 ? (
            <EmptyState title="No campaigns" description="Publish a version, then launch an acknowledgment campaign." />
          ) : (
            <DataTable
              columns={[
                {
                  key: "name",
                  header: "Campaign",
                  render: (c: AcknowledgmentCampaign) => (
                    <Button size="sm" variant="ghost" onClick={() => setSelected(c.id)}>
                      {c.name}
                    </Button>
                  ),
                },
                { key: "status", header: "Status", render: (c: AcknowledgmentCampaign) => <Badge tone={c.status === "open" ? "success" : "neutral"}>{c.status}</Badge> },
                { key: "deadlineAt", header: "Deadline", render: (c: AcknowledgmentCampaign) => formatDate(c.deadlineAt) },
                {
                  key: "actions",
                  header: "",
                  render: (c: AcknowledgmentCampaign) => (
                    <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(async () => { await policyApi.sendReminders(orgId, c.id); })}>
                        Remind
                      </Button>
                      {c.status === "open" && (
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(async () => { await policyApi.closeCampaign(orgId, c.id); state.reload(); })}>
                          Close
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
              rows={campaigns}
              caption="Acknowledgment campaigns"
            />
          )}
        </CardBody>
      </Card>

      {selected && (
        <Card>
          <CardHeader title="Campaign evidence" action={<Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button>} />
          <CardBody>
            {evidence.loading && <LoadingState label="Loading evidence…" />}
            {evidence.error && <ErrorState description={evidence.error.message} onRetry={evidence.reload} />}
            {evidence.data && (
              <DataTable
                columns={[
                  { key: "recipientId", header: "Recipient", render: (a: PolicyAcknowledgment) => memberName(a.recipientId) },
                  { key: "status", header: "Status", render: (a: PolicyAcknowledgment) => <Badge tone={STATUS_TONE[a.status] ?? "neutral"}>{a.status}</Badge> },
                  { key: "completedAt", header: "Completed", render: (a: PolicyAcknowledgment) => formatDate(a.completedAt) },
                  {
                    key: "evidenceHash",
                    header: "Evidence hash",
                    render: (a: PolicyAcknowledgment) => (
                      <code style={{ fontSize: "var(--ods-text-sm)" }}>{a.evidenceHash ?? "None"}</code>
                    ),
                  },
                ]}
                rows={evidence.data.data.acknowledgments}
                caption="Recipient evidence"
              />
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="My acknowledgments" />
        <CardBody>
          {mine.length === 0 ? (
            <EmptyState title="Nothing outstanding" description="You have no policy acknowledgments assigned." />
          ) : (
            <div className="ods-stack">
              {mine.map((a) => (
                <div key={a.id} className="ods-stack" data-testid="my-acknowledgment">
                  <div className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "center" }}>
                    <strong style={{ fontWeight: 600 }}>Version {a.versionId}</strong>
                    <Badge tone={STATUS_TONE[a.status] ?? "neutral"}>{a.status}</Badge>
                  </div>
                  {a.status === "pending" && (
                    <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap" }}>
                      <Field label="Note">
                        {(id) => <TextArea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
                      </Field>
                      <Field label="Refusal reason (required to refuse)">
                        {(id) => <TextInput id={id} value={refusalReason} onChange={(e) => setRefusalReason(e.target.value)} />}
                      </Field>
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await policyApi.completeAcknowledgment(orgId, a.id, {
                              decision: "acknowledged",
                              note: note.trim() || null,
                            });
                            setNote("");
                            state.reload();
                          })
                        }
                      >
                        Acknowledge
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy || !refusalReason.trim()}
                        onClick={() =>
                          run(async () => {
                            await policyApi.completeAcknowledgment(orgId, a.id, {
                              decision: "refused",
                              refusalReason: refusalReason.trim(),
                            });
                            setRefusalReason("");
                            state.reload();
                          })
                        }
                      >
                        Refuse
                      </Button>
                    </div>
                  )}
                  {a.evidenceHash && (
                    <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                      Evidence hash <code>{a.evidenceHash}</code>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
